import Anthropic, { type ClientOptions } from '@anthropic-ai/sdk';
import { ModelBehaviorError, UserError } from '../../errors';
import logger from '../../logger';
import type {
  Model,
  ModelMetadata,
  ModelRequest,
  ModelResponse,
  ModelSettingsReasoningEffort,
  SerializedFunctionTool,
  SerializedHandoff,
  SerializedHostedTool,
  SerializedTool,
} from '../../model';
import { resetCurrentSpan, setCurrentSpan } from '../../tracing/context';
import {
  createGenerationSpan,
  withGenerationSpan,
} from '../../tracing/create-spans';
import { type GenerationSpanData, Span } from '../../tracing/spans';
import type { ResponseStreamEvent } from '../../types/helpers';
import * as protocol from '../../types/protocol';
import { Usage } from '../../usage';

type AnthropicRole = 'user' | 'assistant';

type AnthropicImageBlock = {
  type: 'image';
  source:
    | { type: 'url'; url: string }
    | {
        type: 'base64';
        media_type: string;
        data: string;
      };
  detail?: string;
};

type AnthropicContentBlock =
  | { type: 'text'; text: string }
  | { type: 'thinking'; thinking: string; signature?: string }
  | AnthropicImageBlock
  | {
      type: 'tool_use';
      id: string;
      name: string;
      input: unknown;
    }
  | {
      type: 'tool_result';
      tool_use_id: string;
      content:
        | string
        | Array<{ type: 'text'; text: string } | AnthropicImageBlock>;
      is_error?: boolean;
    };
type AnthropicToolResultContent = Extract<
  AnthropicContentBlock,
  { type: 'tool_result' }
>['content'];
type AnthropicToolResultContentPart = Exclude<
  AnthropicToolResultContent,
  string
>[number];

type AnthropicMessage = {
  role: AnthropicRole;
  content: AnthropicContentBlock[];
};

type AnthropicTool = {
  name: string;
  description?: string;
  input_schema: SerializedFunctionTool['parameters'];
};

type AnthropicRequest = {
  model: string;
  messages: AnthropicMessage[];
  system?: string;
  max_tokens: number;
  stream: boolean;
  temperature?: number;
  top_p?: number;
  tools?: AnthropicTool[];
  tool_choice?: Record<string, unknown>;
  thinking?: Record<string, unknown>;
  output_config?: Record<string, unknown>;
  [key: string]: unknown;
};

type AnthropicUsage = {
  input_tokens?: number;
  output_tokens?: number;
  cache_creation_input_tokens?: number;
  cache_read_input_tokens?: number;
  [key: string]: unknown;
};

type AnthropicResponse = {
  id?: string;
  type?: string;
  role?: 'assistant';
  model?: string;
  content?: Array<Record<string, unknown>>;
  stop_reason?: string | null;
  usage?: AnthropicUsage;
  [key: string]: unknown;
};

type StreamingBlock = {
  index: number;
  type: string;
  text: string;
  thinking: string;
  thinkingStartedAtMs?: number;
  thinkingLastAtMs?: number;
  signature?: string;
  id?: string;
  name?: string;
  input?: unknown;
  inputJson: string;
  partId?: string;
  providerData?: Record<string, unknown>;
};

type StreamingState = {
  id: string;
  usage: AnthropicUsage;
  blocks: Map<number, StreamingBlock>;
  structuredOutput: boolean;
  activeBlockIndex?: number;
  rawResponse?: Record<string, unknown>;
};

export type AnthropicCompatibleFetch = NonNullable<ClientOptions['fetch']>;
export type AnthropicCompatibleClient = Anthropic;

export type AnthropicCompatibleReasoningSettingsResolver = (
  effort: ModelSettingsReasoningEffort | undefined,
  args: { model: string; request: ModelRequest },
) => Pick<AnthropicRequest, 'thinking' | 'output_config'>;

export type AnthropicCompatibleMessagesModelOptions = {
  providerName: string;
  rawModelEventSource: string;
  responseIdFallback: string;
  defaultMaxTokens: number | ((model: string) => number);
  maxOutputTokens: number | ((model: string) => number);
  getReasoningSettings?: AnthropicCompatibleReasoningSettingsResolver;
  supportsImageInput?: (model: string) => boolean;
  imageUnsupportedMessage?: (model: string) => string;
};

export class AnthropicCompatibleMessagesModel implements Model {
  #baseURL: string;
  #client: AnthropicCompatibleClient;
  #model: string;
  #options: AnthropicCompatibleMessagesModelOptions;
  metadata?: ModelMetadata;

  constructor(args: {
    baseURL: string;
    client: AnthropicCompatibleClient;
    model: string;
    metadata?: ModelMetadata;
    options: AnthropicCompatibleMessagesModelOptions;
  }) {
    this.#baseURL = args.baseURL;
    this.#client = args.client;
    this.#model = args.model;
    this.#options = args.options;
    this.metadata = args.metadata;
  }

  async getResponse(request: ModelRequest): Promise<ModelResponse> {
    const fetchResponse = async (
      span: Span<GenerationSpanData> | undefined,
    ) => {
      if (!span) return this.#fetchResponse(request, span, false);

      span.spanData.model = this.#model;
      span.spanData.model_config = request.modelSettings
        ? {
            temperature: request.modelSettings.temperature,
            top_p: request.modelSettings.topP,
            reasoning_effort: request.modelSettings.reasoning?.effort,
          }
        : { base_url: this.#baseURL };
      const response = await this.#fetchResponse(request, span, false);
      if (span && request.tracing === true) {
        span.spanData.output = [response];
      }
      return response;
    };

    const response = request.tracing
      ? await withGenerationSpan(fetchResponse)
      : await fetchResponse(undefined);

    return responseToModelResponse(response, request.outputType);
  }

  async *getStreamedResponse(
    request: ModelRequest,
  ): AsyncIterable<ResponseStreamEvent> {
    const span = request.tracing ? createGenerationSpan() : undefined;
    const state: StreamingState = {
      id: this.#options.responseIdFallback,
      usage: {},
      blocks: new Map(),
      structuredOutput: request.outputType !== 'text',
    };
    let completed = false;

    try {
      if (span) {
        span.start();
        setCurrentSpan(span);
      }
      const stream = await this.#fetchResponse(request, span, true);

      for await (const rawEvent of stream) {
        const event = rawEvent as Record<string, unknown>;
        if (event.type === 'message_start') {
          const message = asRecord(event.message);
          if (typeof message?.id === 'string') {
            state.id = message.id;
          }
          state.rawResponse = message;
          mergeUsage(state.usage, asRecord(message?.usage));
          yield {
            type: 'response_started',
            providerData: event,
          };
        }

        yield {
          type: 'model',
          event,
          providerData: {
            rawModelEventSource: this.#options.rawModelEventSource,
          },
        };

        const normalized = applyAnthropicStreamEvent(state, event);
        for (const normalizedEvent of normalized) {
          yield normalizedEvent;
          if (normalizedEvent.type === 'response_done') {
            completed = true;
          }
        }
      }

      if (!completed) {
        yield buildResponseDoneEvent(state);
      }

      if (span && state.rawResponse && request.tracing === true) {
        span.spanData.output = [state.rawResponse];
      }
    } catch (error) {
      if (span) {
        span.setError({
          message: 'Error streaming response',
          data: {
            error:
              request.tracing === true
                ? String(error)
                : error instanceof Error
                  ? error.name
                  : undefined,
          },
        });
      }
      throw error;
    } finally {
      if (span) {
        span.end();
        resetCurrentSpan();
      }
    }
  }

  async #fetchResponse(
    request: ModelRequest,
    span: Span<GenerationSpanData> | undefined,
    stream: false,
  ): Promise<AnthropicResponse>;
  async #fetchResponse(
    request: ModelRequest,
    span: Span<GenerationSpanData> | undefined,
    stream: true,
  ): Promise<AsyncIterable<Record<string, unknown>>>;
  async #fetchResponse(
    request: ModelRequest,
    span: Span<GenerationSpanData> | undefined,
    stream: boolean,
  ): Promise<AnthropicResponse | AsyncIterable<Record<string, unknown>>> {
    const requestData = buildAnthropicRequest(
      this.#model,
      request,
      stream,
      this.#options,
    );
    if (span && request.tracing === true) {
      span.spanData.input = requestData.messages;
    }

    if (logger.dontLogModelData) {
      logger.debug(
        `Calling ${this.#options.providerName} Anthropic Messages API`,
      );
    } else {
      logger.debug(
        `Calling ${this.#options.providerName} Anthropic Messages API. Request data: ${JSON.stringify(
          requestData,
          null,
          2,
        )}`,
      );
    }

    if (stream) {
      return (await this.#client.messages.create(
        requestData as any,
        request.signal ? { signal: request.signal } : undefined,
      )) as unknown as AsyncIterable<Record<string, unknown>>;
    }

    const body = (await this.#client.messages.create(
      requestData as any,
      request.signal ? { signal: request.signal } : undefined,
    )) as unknown as AnthropicResponse;
    if (logger.dontLogModelData) {
      logger.debug(`${this.#options.providerName} Anthropic response received`);
    } else {
      logger.debug(
        `${this.#options.providerName} Anthropic response received: ${JSON.stringify(body, null, 2)}`,
      );
    }
    return body;
  }
}

function buildAnthropicRequest(
  model: string,
  request: ModelRequest,
  stream: boolean,
  options: AnthropicCompatibleMessagesModelOptions,
): AnthropicRequest {
  const conversion = itemsToAnthropicMessages(request, model, options);
  const tools = [
    ...(request.tools ?? []).map((tool) => toolToAnthropic(tool, options)),
    ...(request.handoffs ?? []).map(handoffToAnthropic),
  ];

  const providerData = request.modelSettings.providerData ?? {};
  const requestData: AnthropicRequest = {
    model,
    messages: conversion.messages,
    system: conversion.system || undefined,
    max_tokens: Math.min(
      request.modelSettings.maxTokens ??
        resolveTokenOption(options.defaultMaxTokens, model),
      resolveTokenOption(options.maxOutputTokens, model),
    ),
    stream,
    temperature: request.modelSettings.temperature,
    top_p: request.modelSettings.topP,
    tools: tools.length > 0 ? tools : undefined,
    tool_choice:
      tools.length > 0
        ? getAnthropicToolChoice(request.modelSettings.toolChoice)
        : undefined,
    ...(options.getReasoningSettings?.(
      request.modelSettings.reasoning?.effort,
      { model, request },
    ) ?? {}),
    ...providerData,
  };

  return requestData;
}

function resolveTokenOption(
  value: number | ((model: string) => number),
  model: string,
): number {
  return typeof value === 'function' ? value(model) : value;
}

function itemsToAnthropicMessages(
  request: ModelRequest,
  model: string,
  options: AnthropicCompatibleMessagesModelOptions,
): {
  messages: AnthropicMessage[];
  system: string;
} {
  const messages: AnthropicMessage[] = [];
  const systemParts: string[] = [];
  if (request.systemInstructions) {
    systemParts.push(request.systemInstructions);
  }

  if (typeof request.input === 'string') {
    pushContent(messages, 'user', [{ type: 'text', text: request.input }]);
    return { messages, system: systemParts.join('\n\n') };
  }

  for (const item of request.input) {
    if ((item as { type?: unknown }).type === 'compaction') {
      throw new UserError(
        `Responses compaction items are not supported with ${options.providerName} Anthropic Messages. Use Runner-level local compaction instead.`,
      );
    }
    if ('role' in item) {
      if (item.role === 'system') {
        systemParts.push(item.content);
      } else if (item.role === 'user') {
        pushContent(
          messages,
          'user',
          userContentToAnthropic(item.content, model, options),
        );
      } else if (item.role === 'assistant') {
        pushContent(messages, 'assistant', assistantContentToAnthropic(item));
      }
      continue;
    }

    if (item.type === 'reasoning') {
      const thinking = item.rawContent?.map((c) => c.text).join('') ?? '';
      if (thinking) {
        pushContent(messages, 'assistant', [
          {
            type: 'thinking',
            thinking,
            ...getThinkingSignature(item.providerData),
          },
        ]);
      }
      continue;
    }

    if (item.type === 'function_call') {
      pushContent(messages, 'assistant', [
        {
          type: 'tool_use',
          id: item.callId,
          name: item.name,
          input: parseJsonObject(item.arguments),
        },
      ]);
      continue;
    }

    if (item.type === 'function_call_result') {
      pushContent(messages, 'user', [
        {
          type: 'tool_result',
          tool_use_id: item.callId,
          content: functionResultOutputToAnthropicToolResultContent(
            item.output,
          ),
          is_error: item.status === 'incomplete' ? true : undefined,
        },
      ]);
      continue;
    }

    if (item.type === 'unknown' && item.providerData) {
      const role = item.providerData.role;
      const content = item.providerData.content;
      if ((role === 'user' || role === 'assistant') && Array.isArray(content)) {
        pushContent(messages, role, content as AnthropicContentBlock[]);
      }
      continue;
    }

    if (item.type === 'hosted_tool_call') {
      throw new UserError(
        `Hosted tool calls are not supported with ${options.providerName} Anthropic Messages.`,
      );
    }
  }

  return { messages, system: systemParts.join('\n\n') };
}

function pushContent(
  messages: AnthropicMessage[],
  role: AnthropicRole,
  content: AnthropicContentBlock[],
): void {
  if (content.length === 0) {
    return;
  }
  const last = messages[messages.length - 1];
  if (last?.role === role) {
    last.content.push(...content);
    return;
  }
  messages.push({ role, content: [...content] });
}

function userContentToAnthropic(
  content: protocol.UserMessageItem['content'],
  model: string,
  options: AnthropicCompatibleMessagesModelOptions,
): AnthropicContentBlock[] {
  if (typeof content === 'string') {
    return [{ type: 'text', text: content }];
  }
  return content.map((part) => {
    if (part.type === 'input_text') {
      return { type: 'text', text: part.text };
    }
    if (part.type === 'input_image') {
      if (!options.supportsImageInput?.(model)) {
        throw new UserError(
          options.imageUnsupportedMessage?.(model) ??
            `${options.providerName} Anthropic Messages does not support image inputs for model ${model}.`,
        );
      }
      return inputImageToAnthropic(part);
    }
    throw new UserError(
      `${options.providerName} Anthropic Messages does not support this user content part.`,
    );
  });
}

function inputImageToAnthropic(part: protocol.InputImage): AnthropicImageBlock {
  if (typeof part.image !== 'string' || part.image.trim().length === 0) {
    throw new UserError(
      'Anthropic-compatible image inputs require a URL, data URL, or base64 string.',
    );
  }

  const image = part.image.trim();
  const source = image.startsWith('data:')
    ? dataUrlToAnthropicImageSource(image)
    : stringToAnthropicImageSource(image);
  return {
    type: 'image',
    source,
    ...(part.detail ? { detail: part.detail } : {}),
  };
}

function stringToAnthropicImageSource(
  image: string,
): Extract<AnthropicContentBlock, { type: 'image' }>['source'] {
  if (/^https?:\/\//i.test(image)) {
    return { type: 'url', url: image };
  }
  return {
    type: 'base64',
    media_type: 'image/jpeg',
    data: image,
  };
}

function dataUrlToAnthropicImageSource(
  image: string,
): Extract<AnthropicContentBlock, { type: 'image' }>['source'] {
  const match = image.match(/^data:([^;,]+);base64,(.+)$/i);
  if (!match) {
    throw new UserError(
      'Anthropic-compatible image data URLs must use base64 encoding.',
    );
  }
  return {
    type: 'base64',
    media_type: match[1]!,
    data: match[2]!,
  };
}

function assistantContentToAnthropic(
  item: protocol.AssistantMessageItem,
): AnthropicContentBlock[] {
  return item.content.map((part) => {
    if (part.type === 'output_text') {
      return { type: 'text', text: part.text };
    }
    if (part.type === 'refusal') {
      return { type: 'text', text: part.refusal };
    }
    throw new UserError(
      'Anthropic-compatible Messages does not support assistant image content.',
    );
  });
}

function toolToAnthropic(
  tool: SerializedTool,
  options: AnthropicCompatibleMessagesModelOptions,
): AnthropicTool {
  if (tool.type !== 'function') {
    throw new UserError(
      `Hosted tools are not supported with ${options.providerName} Anthropic Messages. Got tool type: ${
        (tool as SerializedHostedTool).type
      }`,
    );
  }
  if (typeof tool.namespace === 'string' && tool.namespace.trim().length > 0) {
    throw new UserError(
      `Namespaced function tools are not supported with ${options.providerName} Anthropic Messages.`,
    );
  }
  return {
    name: tool.name,
    description: tool.description || undefined,
    input_schema: tool.parameters,
  };
}

function handoffToAnthropic(handoff: SerializedHandoff): AnthropicTool {
  return {
    name: handoff.toolName,
    description: handoff.toolDescription || undefined,
    input_schema: handoff.inputJsonSchema,
  };
}

function getAnthropicToolChoice(
  toolChoice: ModelRequest['modelSettings']['toolChoice'],
): Record<string, unknown> | undefined {
  if (!toolChoice || toolChoice === 'auto') {
    return undefined;
  }
  if (toolChoice === 'none') {
    return { type: 'none' };
  }
  if (toolChoice === 'required') {
    return { type: 'any' };
  }
  return { type: 'tool', name: toolChoice };
}

function responseToModelResponse(
  response: AnthropicResponse,
  outputType?: ModelRequest['outputType'],
): ModelResponse {
  return {
    usage: new Usage(toUsageData(response.usage)),
    output: contentBlocksToOutput(response.content ?? [], response.id, {
      structuredOutput: outputType !== undefined && outputType !== 'text',
    }),
    responseId: response.id,
    providerData: response,
  };
}

function contentBlocksToOutput(
  content: Array<Record<string, unknown>>,
  responseId?: string,
  options: { structuredOutput?: boolean } = {},
): protocol.OutputModelItem[] {
  const output: protocol.OutputModelItem[] = [];
  const assistantContent: protocol.AssistantContent[] = [];
  const flushAssistantContent = () => {
    if (assistantContent.length === 0) {
      return;
    }
    output.push({
      id: responseId,
      type: 'message',
      role: 'assistant',
      content: normalizeAssistantContentForOutputType(
        assistantContent.splice(0),
        options,
      ),
      status: 'completed',
    });
  };

  for (const block of content) {
    if (block.type === 'thinking') {
      flushAssistantContent();
      const thinking = getString(block.thinking) ?? getString(block.text) ?? '';
      const durationMs = getNonNegativeInteger(block.duration_ms);
      if (thinking) {
        output.push({
          type: 'reasoning',
          content: [],
          rawContent: [
            {
              type: 'reasoning_text',
              text: thinking,
              ...(durationMs !== undefined
                ? { providerData: { duration_ms: durationMs } }
                : {}),
            },
          ],
          providerData: block,
        });
      }
      continue;
    }

    if (block.type === 'text') {
      const text = getString(block.text) ?? '';
      if (text) {
        assistantContent.push({
          type: 'output_text',
          text,
          providerData: block,
        });
      }
      continue;
    }

    if (block.type === 'tool_use') {
      flushAssistantContent();
      output.push({
        id: responseId,
        type: 'function_call',
        callId: getString(block.id) ?? '',
        name: getString(block.name) ?? '',
        arguments: JSON.stringify(block.input ?? {}),
        status: 'completed',
        providerData: block,
      });
      continue;
    }

    flushAssistantContent();
    output.push({
      id: responseId,
      type: 'unknown',
      providerData: block,
    });
  }

  flushAssistantContent();

  return output;
}

function normalizeAssistantContentForOutputType(
  content: protocol.AssistantContent[],
  options: { structuredOutput?: boolean },
): protocol.AssistantContent[] {
  if (!options.structuredOutput) {
    return content;
  }

  const textParts = content.filter((part) => part.type === 'output_text');
  if (textParts.length !== content.length || textParts.length === 0) {
    return content;
  }

  const combinedText = textParts.map((part) => part.text).join('');
  const normalizedText = stripSingleJsonMarkdownFence(combinedText);
  if (normalizedText === combinedText) {
    return content;
  }

  return [
    {
      ...textParts[0]!,
      text: normalizedText,
    },
  ];
}

function stripSingleJsonMarkdownFence(output: string): string {
  const trimmed = output.trim();
  const fencedJson = trimmed.match(
    /^```[ \t]*(?:json|\[json\])?[ \t]*\r?\n([\s\S]*?)\r?\n?```[ \t]*$/i,
  );
  return fencedJson ? fencedJson[1]!.trim() : output;
}

function applyAnthropicStreamEvent(
  state: StreamingState,
  event: Record<string, unknown>,
): ResponseStreamEvent[] {
  const type = event.type;
  if (type === 'content_block_start') {
    const index = Number(event.index ?? 0);
    if (state.activeBlockIndex !== undefined) {
      throw new ModelBehaviorError(
        `Anthropic stream started content block ${index} before content block ${state.activeBlockIndex} stopped.`,
      );
    }
    const contentBlock = asRecord(event.content_block) ?? {};
    const block = getOrCreateStreamingBlock(state, index);
    if (block.partId) {
      throw new ModelBehaviorError(
        `Anthropic stream started duplicate content block ${index}.`,
      );
    }
    block.type = getString(contentBlock.type) ?? 'unknown';
    block.providerData = contentBlock;
    block.partId = getAnthropicOutputPartId(state, block, contentBlock);
    state.activeBlockIndex = index;
    if (block.type === 'text') {
      block.text += getString(contentBlock.text) ?? '';
    } else if (block.type === 'thinking') {
      const thinking = getString(contentBlock.thinking) ?? '';
      if (thinking) recordThinkingDeltaTiming(block);
      block.thinking += thinking;
      block.signature = getString(contentBlock.signature);
    } else if (block.type === 'tool_use') {
      block.id = getString(contentBlock.id);
      block.name = getString(contentBlock.name);
      block.input = contentBlock.input;
    }
    const events: ResponseStreamEvent[] = [
      buildOutputPartStartedEvent(event, block),
    ];
    events.push(...deltasFromInitialBlock(event, block));
    return events;
  }

  if (type === 'content_block_delta') {
    const index = Number(event.index ?? 0);
    const block = getActiveStreamingBlock(state, index, type);
    const delta = asRecord(event.delta) ?? {};
    const deltaType = delta.type;
    if (deltaType === 'text_delta') {
      assertStreamingBlockType(block, 'text', deltaType);
      const text = getString(delta.text) ?? '';
      block.text += text;
      return text
        ? [{ type: 'output_text_delta', delta: text, providerData: event }]
        : [];
    }
    if (deltaType === 'thinking_delta') {
      assertStreamingBlockType(block, 'thinking', deltaType);
      const thinking = getString(delta.thinking) ?? '';
      if (thinking) recordThinkingDeltaTiming(block);
      block.thinking += thinking;
      return thinking
        ? [{ type: 'reasoning_delta', delta: thinking, providerData: event }]
        : [];
    }
    if (deltaType === 'input_json_delta') {
      assertStreamingBlockType(block, 'tool_use', deltaType);
      const inputDelta = getString(delta.partial_json) ?? '';
      block.inputJson += inputDelta;
      return inputDelta
        ? [
            {
              type: 'tool_input_delta',
              partId: requireStreamingBlockPartId(block),
              delta: inputDelta,
              providerData: event,
            },
          ]
        : [];
    } else if (deltaType === 'signature_delta') {
      assertStreamingBlockType(block, 'thinking', deltaType);
      block.signature = `${block.signature ?? ''}${getString(delta.signature) ?? ''}`;
    }
    return [];
  }

  if (type === 'content_block_stop') {
    const index = Number(event.index ?? 0);
    const block = getActiveStreamingBlock(state, index, type);
    state.activeBlockIndex = undefined;
    return [
      {
        type: 'output_part_done',
        partId: requireStreamingBlockPartId(block),
        providerData: event,
      },
    ];
  }

  if (type === 'message_delta') {
    mergeUsage(state.usage, asRecord(event.usage));
    return [];
  }

  if (type === 'message_stop') {
    if (state.activeBlockIndex !== undefined) {
      throw new ModelBehaviorError(
        `Anthropic stream stopped message before content block ${state.activeBlockIndex} stopped.`,
      );
    }
    return [buildResponseDoneEvent(state)];
  }

  return [];
}

function deltasFromInitialBlock(
  event: Record<string, unknown>,
  block: StreamingBlock,
): ResponseStreamEvent[] {
  if (block.type === 'text' && block.text) {
    return [
      { type: 'output_text_delta', delta: block.text, providerData: event },
    ];
  }
  if (block.type === 'thinking' && block.thinking) {
    return [
      { type: 'reasoning_delta', delta: block.thinking, providerData: event },
    ];
  }
  if (block.type === 'tool_use' && hasNonEmptyToolInput(block.input)) {
    return [
      {
        type: 'tool_input_delta',
        partId: requireStreamingBlockPartId(block),
        delta: JSON.stringify(block.input),
        providerData: event,
      },
    ];
  }
  return [];
}

function buildOutputPartStartedEvent(
  event: Record<string, unknown>,
  block: StreamingBlock,
): protocol.StreamEventOutputPartStarted {
  const partId = requireStreamingBlockPartId(block);
  if (block.type === 'text') {
    return {
      type: 'output_part_started',
      partId,
      part: { type: 'output_text' },
      providerData: event,
    };
  }
  if (block.type === 'thinking') {
    return {
      type: 'output_part_started',
      partId,
      part: { type: 'reasoning' },
      providerData: event,
    };
  }
  if (block.type === 'tool_use') {
    if (!block.id || !block.name) {
      throw new ModelBehaviorError(
        `Anthropic stream started tool_use block ${block.index} without id and name.`,
      );
    }
    return {
      type: 'output_part_started',
      partId,
      part: { type: 'function_call', callId: block.id, name: block.name },
      providerData: event,
    };
  }
  throw new ModelBehaviorError(
    `Anthropic stream started unsupported content block type ${block.type}.`,
  );
}

function getAnthropicOutputPartId(
  state: StreamingState,
  block: StreamingBlock,
  contentBlock: Record<string, unknown>,
): string {
  if (block.type === 'tool_use') {
    const id = getString(contentBlock.id);
    if (id) {
      return id;
    }
  }
  return `${state.id}:content_block:${block.index}`;
}

function getActiveStreamingBlock(
  state: StreamingState,
  index: number,
  eventType: unknown,
): StreamingBlock {
  if (state.activeBlockIndex !== index) {
    throw new ModelBehaviorError(
      `Anthropic stream received ${String(eventType)} for content block ${index} while content block ${String(
        state.activeBlockIndex,
      )} is active.`,
    );
  }
  const block = state.blocks.get(index);
  if (!block?.partId) {
    throw new ModelBehaviorError(
      `Anthropic stream received ${String(eventType)} for unknown content block ${index}.`,
    );
  }
  return block;
}

function assertStreamingBlockType(
  block: StreamingBlock,
  expectedType: string,
  deltaType: unknown,
): void {
  if (block.type !== expectedType) {
    throw new ModelBehaviorError(
      `Anthropic stream received ${String(deltaType)} for ${block.type} content block ${block.index}.`,
    );
  }
}

function requireStreamingBlockPartId(block: StreamingBlock): string {
  if (!block.partId) {
    throw new ModelBehaviorError(
      `Anthropic stream content block ${block.index} has no part id.`,
    );
  }
  return block.partId;
}

function hasNonEmptyToolInput(input: unknown): boolean {
  if (input === undefined || input === null) {
    return false;
  }
  if (typeof input === 'object' && !Array.isArray(input)) {
    return Object.keys(input as Record<string, unknown>).length > 0;
  }
  return true;
}

function buildResponseDoneEvent(
  state: StreamingState,
): protocol.StreamEventResponseCompleted {
  const blocks = Array.from(state.blocks.values()).sort(
    (left, right) => left.index - right.index,
  );
  return {
    type: 'response_done',
    response: {
      id: state.id,
      usage: toUsageData(state.usage),
      output: streamingBlocksToOutput(blocks, state.id, {
        structuredOutput: state.structuredOutput,
      }),
    },
  };
}

function streamingBlocksToOutput(
  blocks: StreamingBlock[],
  responseId: string,
  options: { structuredOutput?: boolean } = {},
): protocol.OutputModelItem[] {
  const contentBlocks = blocks.map((block) => {
    if (block.type === 'thinking') {
      return {
        type: 'thinking',
        thinking: block.thinking,
        signature: block.signature,
        index: block.index,
        duration_ms: getThinkingDurationMs(block),
      };
    }
    if (block.type === 'text') {
      return { type: 'text', text: block.text, index: block.index };
    }
    if (block.type === 'tool_use') {
      return {
        type: 'tool_use',
        id: block.id,
        name: block.name,
        input: parseStreamingToolInput(block),
        index: block.index,
      };
    }
    return {
      type: block.type,
      index: block.index,
      providerData: block.providerData,
    };
  });
  return contentBlocksToOutput(contentBlocks, responseId, options);
}

function getOrCreateStreamingBlock(
  state: StreamingState,
  index: number,
): StreamingBlock {
  let block = state.blocks.get(index);
  if (!block) {
    block = {
      index,
      type: 'unknown',
      text: '',
      thinking: '',
      inputJson: '',
    };
    state.blocks.set(index, block);
  }
  return block;
}

function recordThinkingDeltaTiming(block: StreamingBlock): void {
  const now = Date.now();
  block.thinkingStartedAtMs ??= now;
  block.thinkingLastAtMs = now;
}

function getThinkingDurationMs(block: StreamingBlock): number | undefined {
  if (
    block.thinkingStartedAtMs === undefined ||
    block.thinkingLastAtMs === undefined
  ) {
    return undefined;
  }
  return Math.max(0, block.thinkingLastAtMs - block.thinkingStartedAtMs);
}

function parseStreamingToolInput(block: StreamingBlock): unknown {
  if (block.inputJson.trim().length > 0) {
    return parseJsonObject(block.inputJson);
  }
  return block.input ?? {};
}

function toUsageData(usage?: AnthropicUsage): protocol.UsageData {
  const inputTokens = Number(usage?.input_tokens ?? 0);
  const outputTokens = Number(usage?.output_tokens ?? 0);
  return {
    inputTokens,
    outputTokens,
    totalTokens: inputTokens + outputTokens,
    inputTokensDetails: {
      cache_creation_input_tokens: Number(
        usage?.cache_creation_input_tokens ?? 0,
      ),
      cache_read_input_tokens: Number(usage?.cache_read_input_tokens ?? 0),
    },
    outputTokensDetails: {},
  };
}

function mergeUsage(
  target: AnthropicUsage,
  source: Record<string, unknown> | undefined,
): void {
  if (!source) {
    return;
  }
  for (const [key, value] of Object.entries(source)) {
    if (typeof value === 'number') {
      target[key] = value;
    }
  }
}

function parseJsonObject(value: string): unknown {
  try {
    return JSON.parse(value || '{}');
  } catch {
    return value;
  }
}

function functionResultOutputToText(
  output: protocol.FunctionCallResultItem['output'],
): string {
  if (typeof output === 'string') {
    return output;
  }
  if (Array.isArray(output)) {
    return output
      .map((part) =>
        part.type === 'input_text' ? part.text : JSON.stringify(part),
      )
      .join('\n');
  }
  if (output.type === 'text') {
    return output.text;
  }
  return JSON.stringify(output);
}

function functionResultOutputToAnthropicToolResultContent(
  output: protocol.FunctionCallResultItem['output'],
): AnthropicToolResultContent {
  if (typeof output === 'string') {
    return output;
  }
  if (Array.isArray(output)) {
    return output.map((part): AnthropicToolResultContentPart => {
      if (part.type === 'input_text') {
        return { type: 'text', text: part.text };
      }
      if (part.type === 'input_image') {
        return inputImageToAnthropic(part);
      }
      return { type: 'text', text: JSON.stringify(part) };
    });
  }
  if (output.type === 'text') {
    return output.text;
  }
  if (output.type === 'image') {
    return [inputImageToAnthropic(legacyImageOutputToInputImage(output))];
  }
  return functionResultOutputToText(output);
}

function legacyImageOutputToInputImage(
  output: protocol.ToolOutputImage,
): protocol.InputImage {
  const image = output.image;
  if (typeof image === 'string') {
    return {
      type: 'input_image',
      image,
      providerData: output.providerData,
    };
  }
  if (image && typeof image === 'object') {
    if ('url' in image && typeof image.url === 'string') {
      return {
        type: 'input_image',
        image: image.url,
        providerData: output.providerData,
      };
    }
    if ('data' in image) {
      const mediaType =
        typeof image.mediaType === 'string' ? image.mediaType : 'image/jpeg';
      const data =
        typeof image.data === 'string'
          ? image.data
          : Buffer.from(image.data).toString('base64');
      return {
        type: 'input_image',
        image: data.startsWith('data:')
          ? data
          : `data:${mediaType};base64,${data}`,
        providerData: output.providerData,
      };
    }
  }
  throw new UserError(
    `Anthropic-compatible image tool outputs require string image data, data objects, or URLs: ${JSON.stringify(output)}`,
  );
}

function getThinkingSignature(
  providerData: Record<string, unknown> | undefined,
): { signature?: string } {
  const signature = providerData?.signature;
  return typeof signature === 'string' ? { signature } : {};
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : undefined;
}

function getString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function getNonNegativeInteger(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0
    ? value
    : undefined;
}

// function trimTrailingSlash(value: string): string {
//   return value.replace(/\/+$/, "");
// }
